import React from 'react';
import { PCCase } from '../../types';
import { useAppStore } from '../../context/store';
import { formatDimensionValue } from '../../utils/conversions';
import { getSeriesColor } from '../../utils/seriesColors';

interface ComparisonView2DProps {
  cases: PCCase[];
  overlayMode?: boolean;
  showGrid?: boolean;
}

export const ComparisonView2D: React.FC<ComparisonView2DProps> = ({
  cases: allCases,
  overlayMode = false,
  showGrid = true,
}) => {
  const { comparison } = useAppStore();
  const unit = comparison.unitSystem === 'metric' ? 'mm' : 'in';
  const cases = allCases.filter((c) => c.dimensions);

  if (cases.length === 0) {
    return (
      <div className="flex h-full w-full items-center justify-center bg-surface-2 text-sm text-muted">
        No dimension data for these cases.
      </div>
    );
  }

  // Calculate scale to fit all cases
  const maxHeight = Math.max(...cases.map((c) => c.dimensions.height));
  const maxWidth = Math.max(...cases.map((c) => c.dimensions.width));

  // SVG dimensions
  const viewWidth = 800;
  const viewHeight = 500;
  const padding = 60;

  // Calculate scale factor
  const totalWidth = overlayMode
    ? maxWidth
    : cases.reduce((sum, c) => sum + c.dimensions.width, 0) + (cases.length - 1) * 50;

  const scaleX = (viewWidth - padding * 2) / totalWidth;
  const scaleY = (viewHeight - padding * 2) / maxHeight;
  const scale = Math.min(scaleX, scaleY);

  // Grid settings
  const gridStep = unit === 'mm' ? 50 : 25.4; // 50mm or 1 inch

  const renderGrid = () => {
    if (!showGrid) return null;

    const lines = [];
    const gridSize = maxHeight > maxWidth ? maxHeight : maxWidth;
    const numLines = Math.ceil(gridSize / gridStep) + 1;

    for (let i = 0; i <= numLines; i++) {
      const pos = i * gridStep * scale;
      const major = i % 5 === 0;
      const lineClass = major ? 'stroke-line-strong' : 'stroke-line';

      lines.push(
        <line
          key={`h-${i}`}
          x1={padding}
          y1={viewHeight - padding - pos}
          x2={viewWidth - padding}
          y2={viewHeight - padding - pos}
          className={lineClass}
          strokeWidth={major ? 1 : 0.5}
          strokeDasharray={major ? 'none' : '2,2'}
        />
      );

      lines.push(
        <line
          key={`v-${i}`}
          x1={padding + pos}
          y1={padding}
          x2={padding + pos}
          y2={viewHeight - padding}
          className={lineClass}
          strokeWidth={major ? 1 : 0.5}
          strokeDasharray={major ? 'none' : '2,2'}
        />
      );
    }

    return <g className="grid-lines">{lines}</g>;
  };

  const renderCase = (pcCase: PCCase, index: number) => {
    const color = getSeriesColor(index);
    const width = pcCase.dimensions.width * scale;
    const height = pcCase.dimensions.height * scale;

    // Calculate X position - align to right in overlay mode
    let x = padding;
    if (overlayMode) {
      const maxWidthScaled = maxWidth * scale;
      x = padding + (maxWidthScaled - width);
    } else {
      for (let i = 0; i < index; i++) {
        x += cases[i].dimensions.width * scale + 50 * scale;
      }
    }

    // Y position - all cases aligned to bottom
    const y = viewHeight - padding - height;

    return (
      <g key={pcCase.id} className="case-silhouette">
        {/* Case body */}
        <rect x={x} y={y} width={width} height={height} fill={color} fillOpacity={0.1} stroke={color} strokeWidth={1.75} rx={3} />

        {/* Front panel detail */}
        <line x1={x + width * 0.18} y1={y + height * 0.06} x2={x + width * 0.82} y2={y + height * 0.06} stroke={color} strokeWidth={1.5} strokeOpacity={0.6} />
        <rect
          x={x + width * 0.14}
          y={y + height * 0.2}
          width={width * 0.72}
          height={height * 0.72}
          fill="none"
          stroke={color}
          strokeOpacity={0.45}
          strokeWidth={1}
          strokeDasharray="4,3"
          rx={2}
        />

        {/* Label - only show in non-overlay mode */}
        {!overlayMode && (
          <text x={x + width / 2} y={y - 12} className="fill-fg" fontSize="12" fontWeight="600" textAnchor="middle">
            {pcCase.name}
          </text>
        )}

        {/* Width */}
        <g className="dimension-width">
          <line x1={x} y1={y + height + 18} x2={x + width} y2={y + height + 18} stroke={color} strokeWidth={1} />
          <line x1={x} y1={y + height + 13} x2={x} y2={y + height + 23} stroke={color} strokeWidth={1} />
          <line x1={x + width} y1={y + height + 13} x2={x + width} y2={y + height + 23} stroke={color} strokeWidth={1} />
          <text x={x + width / 2} y={y + height + 34} className="fill-muted" fontSize="10" fontFamily="'Geist Mono', monospace" textAnchor="middle">
            {formatDimensionValue(pcCase.dimensions.width, unit)} {unit}
          </text>
        </g>

        {/* Height */}
        <g className="dimension-height">
          <line x1={x - 18} y1={y} x2={x - 18} y2={y + height} stroke={color} strokeWidth={1} />
          <line x1={x - 13} y1={y} x2={x - 23} y2={y} stroke={color} strokeWidth={1} />
          <line x1={x - 13} y1={y + height} x2={x - 23} y2={y + height} stroke={color} strokeWidth={1} />
          <text
            x={x - 28}
            y={y + height / 2}
            className="fill-muted"
            fontSize="10"
            fontFamily="'Geist Mono', monospace"
            textAnchor="middle"
            transform={`rotate(-90, ${x - 28}, ${y + height / 2})`}
          >
            {formatDimensionValue(pcCase.dimensions.height, unit)} {unit}
          </text>
        </g>
      </g>
    );
  };

  return (
    <div className="h-full w-full overflow-hidden bg-surface-2">
      <svg
        viewBox={`0 0 ${viewWidth} ${viewHeight}`}
        className="block h-full w-full"
        preserveAspectRatio="xMidYMid meet"
        role="img"
        aria-label={`Front view size comparison of ${cases.map((c) => `${c.manufacturer} ${c.name}`).join(', ')}`}
      >
        {/* Grid */}
        {renderGrid()}

        {/* Scale reference */}
        <g className="scale-reference">
          <line
            x1={viewWidth - padding}
            y1={viewHeight - padding}
            x2={viewWidth - padding}
            y2={viewHeight - padding - 100 * scale}
            className="stroke-muted"
            strokeWidth={2}
          />
          <text
            x={viewWidth - padding + 8}
            y={viewHeight - padding - 50 * scale}
            className="fill-muted"
            fontSize="10"
            fontFamily="'Geist Mono', monospace"
          >
            {unit === 'mm' ? '100 mm' : '~4"'}
          </text>
        </g>

        {/* Cases - render largest first in overlay so the smallest stays on top */}
        {overlayMode
          ? [...cases]
              .sort((a, b) => b.dimensions.height * b.dimensions.width - a.dimensions.height * a.dimensions.width)
              .map((pcCase) => renderCase(pcCase, cases.findIndex((c) => c.id === pcCase.id)))
          : cases.map((pcCase, index) => renderCase(pcCase, index))}

        {/* Legend for overlay mode */}
        {overlayMode && (
          <g className="legend">
            {cases.map((pcCase, index) => (
              <g key={pcCase.id} transform={`translate(${padding}, ${padding + index * 20})`}>
                <rect width={12} height={12} fill={getSeriesColor(index)} fillOpacity={0.15} stroke={getSeriesColor(index)} strokeWidth={1.5} rx={2} />
                <text x={18} y={10} className="fill-fg" fontSize="11" fontWeight="500">
                  {pcCase.manufacturer} {pcCase.name}
                </text>
              </g>
            ))}
          </g>
        )}
      </svg>
    </div>
  );
};
